// Save as: src/pages/organizations/OranizationDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Divider,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

// Bring back Bar for HTTP Methods (web/api)
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

import DomainIcon from '@mui/icons-material/Domain';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// New icons
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';   // Assets
import SettingsEthernetOutlinedIcon from '@mui/icons-material/SettingsEthernetOutlined'; // Ports
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';     // Findings
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
import PhoneCallbackOutlinedIcon from '@mui/icons-material/PhoneCallbackOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import ErrorNotification from '../../components/ErrorNotification';
import { BASEURL } from '../../Constants';
import Nav from './nav';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const brandColor = '#005CC5';

const gradePalette = {
  'A+':'#4caf50', 'A-':'#4caf50', B:'#2196f3',
  C:'#ff9800', D:'#ffeb3b', F:'#f44336', '?':'#9e9e9e'
};

// ===== Helpers =====
const TYPE_META = {
  external: { label: 'External', icon: PublicIcon, color: '#10b981' },
  web:      { label: 'Web',      icon: LanguageIcon, color: '#3b82f6' },
  api:      { label: 'API',      icon: LanguageIcon, color: '#06b6d4' },
  vishing:  { label: 'Vishing',  icon: PhoneIcon,    color: '#f59e0b' },
  darkweb:  { label: 'Darkweb',  icon: LockIcon,     color: '#8b5cf6' },
};
const TYPE_KEYS = ['external', 'web', 'api', 'vishing', 'darkweb'];

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
const SEV_COLORS = {
  critical: '#7e22ce',
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#3b82f6',
  info: '#10b981',
};

const getGrade = (score) => {
  if (score === null || score === undefined) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};
const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-': return '#16a34a';
    case 'B':  return '#2563eb';
    case 'C':  return '#f59e0b';
    case 'D':  return '#eab308';
    case 'F':  return '#ef4444';
    default:   return '#9e9e9e';
  }
};
const getGradeMessage = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-': return 'Good job! Keep up the good work.';
    case 'B':  return 'Solid posture—tighten the gaps to reach A.';
    case 'C':  return 'Moderate risk. Prioritize remediation.';
    case 'D':  return 'Elevated risk. Act on critical/high findings.';
    case 'F':  return 'Critical risk. Immediate remediation required.';
    default:   return '';
  }
};

// Period starts
const startOfDay = (date) => { const d = new Date(date); d.setHours(0,0,0,0); return d; };
const startOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfQuarter = (date) => {
  const d = new Date(date);
  const q = Math.floor(d.getMonth() / 3) * 3;
  d.setMonth(q, 1);
  d.setHours(0,0,0,0);
  return d;
};
const startOfYear    = (date) => {
  const d = new Date(date);
  d.setMonth(0, 1);
  d.setHours(0,0,0,0);
  return d;
};

const periodStartFns = {
  day: startOfDay,
  week: startOfWeek,
  month: (d) => { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; },
  quarter: startOfQuarter,
  year: startOfYear,
};

const filterByWindow = (pentests, granularity, windowSize) => {
  const now = new Date();
  const unitDays = { day: 1, week: 7, month: 30, quarter: 91, year: 365 };
  const days = (unitDays[granularity] || 30) * (windowSize || 12);
  const threshold = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  return pentests.filter((p) => {
    const createdAt = p.created_at || p.createdAt || p.createdAtUtc || p.createdAtISO;
    if (!createdAt) return false;
    const dt = new Date(createdAt);
    return !isNaN(dt.getTime()) && dt >= threshold;
  });
};

const computeTotals = (data) => {
  const totalPentests = data.length;
  let sumRisk = 0, riskN = 0, totalFindings = 0;
  const byType = {};
  TYPE_KEYS.forEach((k) => (byType[k] = { count: 0, findings: 0, sumRisk: 0, riskN: 0 }));

  data.forEach((p) => {
    const tKey = (p.type || 'external').toLowerCase();
    if (!byType[tKey]) byType[tKey] = { count: 0, findings: 0, sumRisk: 0, riskN: 0 };
    byType[tKey].count += 1;

    const r = Number(p.risk_score);
    if (Number.isFinite(r)) { sumRisk += r; riskN += 1; byType[tKey].sumRisk += r; byType[tKey].riskN += 1; }

    const f = Number(p.exploits_count ?? 0);
    if (Number.isFinite(f)) { totalFindings += f; byType[tKey].findings += f; }
  });

  const byTypeFinal = Object.fromEntries(
    Object.entries(byType).map(([k, v]) => [
      k,
      { count: v.count, findings: v.findings, avgRisk: v.riskN ? v.sumRisk / v.riskN : null },
    ])
  );

  return { totalPentests, avgRisk: riskN ? sumRisk / riskN : null, totalFindings, byType: byTypeFinal };
};

const StatCard = ({ icon, title, value, subtitle, gradient = false, color }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      height: '100%',
      background: gradient
        ? `linear-gradient(135deg, ${brandColor} 0%, #003a88 100%)`
        : (t) => alpha(t.palette.background.paper, 1),
      color: gradient ? 'white' : 'inherit',
      boxShadow: gradient ? '0 10px 40px rgba(0,0,0,0.15)' : 'none',
      border: color ? `1px solid ${alpha(color, 0.35)}` : undefined,
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: color ? alpha(color, 0.12) : (gradient ? alpha('#fff', 0.2) : alpha(brandColor, 0.1)), color: color || 'inherit' }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// === Restored GradeCard (original style) ===
const GradeCard = ({ typeKey, avg }) => {
  const grade = getGrade(avg);
  const color = getGradeColor(grade);
  const Meta = TYPE_META[typeKey];
  return (
    <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(Meta.color, 0.15), color: Meta.color }}>
            {React.createElement(Meta.icon)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" sx={{ opacity: 0.7 }}>{Meta.label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {avg != null ? `${Math.round(avg)}/100` : '—'}
            </Typography>
            <Chip
              size="small"
              label={`Grade: ${grade}`}
              sx={{
                mt: 0.5,
                bgcolor: alpha(color, 0.12),
                color,
                fontWeight: 700,
              }}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Used in section headers for the per-type average + message
const GradePill = ({ score }) => {
  const grade = getGrade(score);
  const color = getGradeColor(grade);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip size="small" label={`Avg Grade: ${grade}`} sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {getGradeMessage(grade)}
      </Typography>
    </Stack>
  );
};

const norm = (v) => (v == null ? 0 : Number(v) || 0);
const normSev = (s) => String(s || '').trim().toLowerCase();
const collectType = (pentests, typeKey) => pentests.filter((p) => String(p.type || '').toLowerCase() === typeKey);

// Build findings helpers — cap to 10
const buildRecentFindings = (items, take = 10) => {
  const rows = [];
  items.forEach((p) => {
    const when = p.created_at ? new Date(p.created_at) : null;
    const exList = p?.stats?.exploits_list;
    if (Array.isArray(exList)) {
      exList.forEach((e) => rows.push({ name: e.name || 'Finding', severity: normSev(e.severity), when, pentestId: p.id }));
    }
    if (String(p.type).toLowerCase() === 'darkweb' && p?.stats?.database_leaks) {
      Object.entries(p.stats.database_leaks).forEach(([src, count]) =>
        rows.push({ name: `${src} leak`, severity: 'high', count: Number(count) || 1, when, pentestId: p.id })
      );
    }
    if (String(p.type).toLowerCase() === 'vishing' && Array.isArray(p?.stats?.users)) {
      p.stats.users
        .filter((u) => String(u.passOrFail || '').toLowerCase() === 'failed')
        .forEach((u) => rows.push({ name: `User failed: ${u.name} (${u.scenario || 'scenario'})`, severity: 'high', when, pentestId: p.id }));
    }
  });
  rows.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));
  return rows.slice(0, take);
};
const buildTopFindings = (items, take = 10) => {
  const map = new Map();
  items.forEach((p) => {
    const exList = p?.stats?.exploits_list;
    if (Array.isArray(exList)) {
      exList.forEach((e) => {
        const key = `${(e.name || 'Finding').toLowerCase()}|${normSev(e.severity)}`;
        map.set(key, (map.get(key) || { name: e.name || 'Finding', severity: normSev(e.severity), count: 0 }));
        map.get(key).count += 1;
      });
    }
  });
  const arr = Array.from(map.values());
  const order = (sev) => {
    const idx = SEV_ORDER.indexOf(normSev(sev));
    return idx === -1 ? SEV_ORDER.length : idx;
  };
  arr.sort((a, b) => (order(a.severity) - order(b.severity)) || ((b.count || 0) - (a.count || 0)));
  return arr.slice(0, take);
};

// HTTP methods bar data
const httpMethodsBar = (methods) => {
  const labels = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  const data = labels.map((k) => methods?.[k] ?? 0);
  return {
    labels,
    datasets: [{
      label: 'HTTP Methods',
      data,
      backgroundColor: alpha('#111', 0.08),
      borderColor: alpha('#111', 0.25),
      borderWidth: 1,
    }],
  };
};

// ===== Component =====
const OranizationDashboard = () => {
  const navigate = useNavigate();
  const { id: organizationId } = useParams();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pentestsAll, setPentestsAll] = useState([]);

  // Controls (add Day option)
  const [granularity, setGranularity] = useState('month'); // day | week | month | quarter | year
  const [windowSize, setWindowSize] = useState(12);
  useEffect(() => {
    const defaults = { day: 14, week: 12, month: 12, quarter: 8, year: 5 };
    setWindowSize(defaults[granularity] || 12);
  }, [granularity]);

  const fetchPentests = () => {
    const token = sessionStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    if (!organizationId) { setError('Missing organization id'); return; }

    setLoading(true);
    fetch(`${BASEURL}/organization/stats?organization_id=${encodeURIComponent(organizationId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.error) {
          setError(res.message || 'Error fetching organization pentests');
          setPentestsAll([]);
        } else {
          setPentestsAll(res.pentests || []);
          setError(null);
        }
      })
      .catch(() => setError('Error fetching organization pentests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPentests(); /* eslint-disable-next-line */ }, [organizationId]);

  // Filtered data (applies to EVERYTHING)
  const pentests = useMemo(() => filterByWindow(pentestsAll, granularity, windowSize), [pentestsAll, granularity, windowSize]);

  // Aggregates
  const totals = useMemo(() => computeTotals(pentests), [pentests]);
  const avgByType = TYPE_KEYS.reduce((acc, k) => { acc[k] = totals.byType?.[k]?.avgRisk ?? null; return acc; }, {});

  // Type buckets
  const extItems = useMemo(() => collectType(pentests, 'external'), [pentests]);
  const webItems = useMemo(() => collectType(pentests, 'web'), [pentests]);
  const apiItems = useMemo(() => collectType(pentests, 'api'), [pentests]);
  const vishingItems = useMemo(() => collectType(pentests, 'vishing'), [pentests]);
  const darkwebItems = useMemo(() => collectType(pentests, 'darkweb'), [pentests]);

  // External rollup (plus ports/tech names only)
  const externalRollup = useMemo(() => {
    const totals = {
      ports: 0, assets: 0, exploits: 0,
      cves: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
      exploitsBySev: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
      portsList: [], techList: [],
    };
    const portSet = new Set();
    const techSet = new Set();
    extItems.forEach((p) => {
      const c = p?.stats?.counts || {};
      totals.assets += norm(c.assets);
      totals.ports  += norm(c.open_ports);
      totals.exploits += norm(c.exploits);
      totals.exploitsBySev.critical += norm(c.critical_exploits);
      totals.exploitsBySev.high     += norm(c.high_exploits);
      totals.exploitsBySev.medium   += norm(c.medium_exploits);
      totals.exploitsBySev.low      += norm(c.low_exploits);
      totals.exploitsBySev.info     += norm(c.info_exploits);
      totals.cves.critical += norm(c.critical_cves);
      totals.cves.high     += norm(c.high_cves);
      totals.cves.medium   += norm(c.medium_cves);
      totals.cves.low      += norm(c.low_cves);
      totals.cves.info     += norm(c.info_cves);

      (p?.stats?.groupings?.ports_distribution || []).forEach(({ port }) => portSet.add(String(port)));
      (p?.stats?.groupings?.technologies_distribution || []).forEach(({ technology }) => {
        const key = String(technology).replace(/:\s*$/, '').trim();
        if (key) techSet.add(key);
      });
    });
    totals.portsList = Array.from(portSet).slice(0, 40);
    totals.techList  = Array.from(techSet).slice(0, 40);
    return totals;
  }, [extItems]);

  // Web/API rollups (plus methods for bar charts)
  const webRollup = useMemo(() => {
    const acc = { endpoints: 0, exploits: { critical: 0, high: 0, medium: 0, low: 0, info: 0 }, methods: { GET: 0, POST: 0, PUT: 0, DELETE: 0, PATCH: 0 } };
    webItems.forEach((p) => {
      const s = p?.stats || {};
      acc.endpoints += norm(s.total_endpoints);
      acc.exploits.critical += norm(s.total_critical_exploits);
      acc.exploits.high     += norm(s.total_high_exploits ?? s['total_high_exploit:s']);
      acc.exploits.medium   += norm(s.total_medium_exploits);
      acc.exploits.low      += norm(s.total_low_exploits);
      acc.exploits.info     += norm(s.total_info_exploits);
      acc.methods.GET       += norm(s.total_get_methods);
      acc.methods.POST      += norm(s.total_post_methods);
      acc.methods.PUT       += norm(s.total_put_methods);
      acc.methods.DELETE    += norm(s.total_delete_methods);
      acc.methods.PATCH     += norm(s.total_patch_methods);
    });
    return acc;
  }, [webItems]);

  const apiRollup = useMemo(() => {
    const acc = { endpoints: 0, exploits: { critical: 0, high: 0, medium: 0, low: 0, info: 0 }, methods: { GET: 0, POST: 0, PUT: 0, DELETE: 0, PATCH: 0 } };
    apiItems.forEach((p) => {
      const s = p?.stats || {};
      acc.endpoints += norm(s.total_endpoints);
      acc.exploits.critical += norm(s.total_critical_exploits);
      acc.exploits.high     += norm(s.total_high_exploits ?? s['total_high_exploit:s']);
      acc.exploits.medium   += norm(s.total_medium_exploits);
      acc.exploits.low      += norm(s.total_low_exploits);
      acc.exploits.info     += norm(s.total_info_exploits);
      acc.methods.GET       += norm(s.total_get_methods);
      acc.methods.POST      += norm(s.total_post_methods);
      acc.methods.PUT       += norm(s.total_put_methods);
      acc.methods.DELETE    += norm(s.total_delete_methods);
      acc.methods.PATCH     += norm(s.total_patch_methods);
    });
    return acc;
  }, [apiItems]);

  // Vishing rollups
  const vishingRollup = useMemo(() => {
    const acc = {
      totalUsers: 0, passed: 0, failed: 0,
      totalCalls: 0, endedCalls: 0, ongoingCalls: 0,
      scenarios: new Set(), voices: new Set(),
      scenariosList: [], voicesList: [],
      recentFailedUsers: [],
      totalCampaigns: vishingItems.length,
    };
    const failedRows = [];
    vishingItems.forEach((p) => {
      const s = p?.stats || {};
      acc.totalUsers += norm(s.total_users);
      acc.passed     += norm(s.passed_users);
      acc.failed     += norm(s.failed_users);
      acc.totalCalls += norm(s.total_calls);
      acc.endedCalls += norm(s.ended_calls);
      acc.ongoingCalls += norm(s.ongoing_calls);
      (s.scenarios || []).forEach((sc) => acc.scenarios.add(sc.name || sc.id));
      (s.voices || []).forEach((v) => acc.voices.add(v.name || v.id));
      const when = p.created_at ? new Date(p.created_at) : null;
      (s.users || []).forEach((u) => {
        if (String(u.passOrFail || '').toLowerCase() === 'failed') {
          failedRows.push({ name: u.name || 'User', scenario: u.scenario, voice: u.voice, when });
        }
      });
    });
    failedRows.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));
    acc.recentFailedUsers = failedRows.slice(0, 10);
    acc.scenariosList = Array.from(acc.scenarios).slice(0, 20);
    acc.voicesList = Array.from(acc.voices).slice(0, 20);
    return acc;
  }, [vishingItems]);

  // Darkweb rollups
  const darkwebRollup = useMemo(() => {
    const acc = { totalLeaks: 0, totalEmails: 0, clearText: 0, hashed: 0, sources: {} };
    darkwebItems.forEach((p) => {
      const s = p?.stats || {};
      acc.totalLeaks  += norm(s.total_leaks);
      acc.totalEmails += norm(s.total_emails);
      acc.clearText   += norm(s.clear_text_passwords);
      acc.hashed      += norm(s.hashed_passwords);
      Object.entries(s.database_leaks || {}).forEach(([k, v]) => { acc.sources[k] = (acc.sources[k] || 0) + (Number(v) || 0); });
    });
    const sourceRows = Object.entries(acc.sources)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    return { ...acc, sourceRows };
  }, [darkwebItems]);

  // Tables (cap to 10)
  const recentExternal = useMemo(() => buildRecentFindings(extItems, 10), [extItems]);
  const topExternal    = useMemo(() => buildTopFindings(extItems, 10), [extItems]);
  const recentWeb      = useMemo(() => buildRecentFindings(webItems, 10), [webItems]);
  const topWeb         = useMemo(() => buildTopFindings(webItems, 10), [webItems]);
  const recentAPI      = useMemo(() => buildRecentFindings(apiItems, 10), [apiItems]);
  const topAPI         = useMemo(() => buildTopFindings(apiItems, 10), [apiItems]);

  // Recent 5 pentests per type
  const recentPentestsFor = (items) =>
    [...items].sort((a,b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0,5)
      .map((p) => ({ id: p.id, name: p.name || `Pentest #${p.id}`, grade: getGrade(p.risk_score), findings: Number(p.exploits_count ?? 0) }));
  const extRecent5  = useMemo(() => recentPentestsFor(extItems), [extItems]);
  const webRecent5  = useMemo(() => recentPentestsFor(webItems), [webItems]);
  const apiRecent5  = useMemo(() => recentPentestsFor(apiItems), [apiItems]);
  const visRecent5  = useMemo(() => recentPentestsFor(vishingItems), [vishingItems]);
  const darkRecent5 = useMemo(() => recentPentestsFor(darkwebItems), [darkwebItems]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: (t) => alpha(t.palette.grey[100], 0.8) }}>
      <ErrorNotification errorMessage={error} />
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Nav organizationId={organizationId} page="dashboard" />

        {/* ============================================
          Company Overview (filters + grade/metrics + type grades)
          ============================================ */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        {/* Header + Filters on TOP of the section */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Company Overview
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {/* Granularity */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" sx={{ opacity: 0.7 }}>Granularity</Typography>
              <ToggleButtonGroup
                exclusive
                value={granularity}
                onChange={(_, val) => val && setGranularity(val)}
                size="small"
                sx={{
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, px: 1.2, borderColor: 'transparent'
                  },
                  '& .Mui-selected': {
                    bgcolor: alpha(brandColor, 0.08),
                    border: '1.5px solid ' + alpha(brandColor, 0.35),
                    color: brandColor,
                  },
                }}
              >
                <ToggleButton value="day">Day</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="quarter">Quarter</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {/* Periods */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" sx={{ opacity: 0.7 }}>Periods</Typography>
              <Select size="small" value={windowSize} onChange={(e) => setWindowSize(Number(e.target.value))} sx={{ minWidth: 96 }}>
                {[7, 10, 12, 14, 16, 20, 24, 36].map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
              </Select>
              <Tooltip title="Reload data">
                <IconButton onClick={fetchPentests}><RefreshIcon /></IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>

        {/* ── grade card + metrics ───────────────────────── */}
        {(() => {
          const grade = getGrade(totals.avgRisk);
          const gradeColor = gradePalette[grade] || '#9e9e9e';
          const gradeMessage = getGradeMessage(grade);

          return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Grade card FIRST and most prominent */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: alpha(gradeColor, 0.10),
                    borderLeft: `8px solid ${gradeColor}`,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      minHeight: 160,
                    }}
                  >
                    <Typography variant="h1" sx={{ color: gradeColor, fontWeight: 800, lineHeight: 1 }}>
                      {grade}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      {gradeMessage}
                    </Typography>
                    {/* Optional supporting chip showing numeric risk */}
                    {totals.avgRisk != null && (
                      <Chip
                        label={`Overall Risk: ${Math.round(totals.avgRisk)}/100`}
                        size="small"
                        sx={{ mt: 1.5, bgcolor: alpha(gradeColor, 0.15), color: gradeColor, fontWeight: 700 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Normal metric cards to the right */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={<InsightsIcon />}
                  title="Total Pentests (window)"
                  value={totals.totalPentests}
                  subtitle="All types in selected window"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={<AssessmentIcon />}
                  title="Total Findings (window)"
                  value={totals.totalFindings}
                  subtitle="Sum across all pentests"
                />
              </Grid>
            </Grid>
          );
        })()}

        <Divider sx={{ my: 2 }} />

        {/* === Grades by Pentest Type (original look, under the metrics) === */}
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Grades by Pentest Type</Typography>
        {loading ? (
          <Box sx={{ p: 1 }}>{[...Array(5)].map((_, i) => (<Skeleton key={i} variant="rounded" height={96} sx={{ mb: 1 }} />))}</Box>
        ) : (
          <Grid container spacing={2}>
            {TYPE_KEYS.map((k) => (
              <Grid item xs={12} sm={6} md={2.4} key={k}>
                <GradeCard typeKey={k} avg={totals.byType?.[k]?.avgRisk ?? null} />
              </Grid>
            ))}
          </Grid>
        )}
      </Card>

        {/* ===== Per-type mini dashboards ===== */}
        <Box sx={{ mt: 3 }}>
          {/* External */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(TYPE_META.external.color, 0.12), color: TYPE_META.external.color }}>
                  <PublicIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>External</Typography>
              </Stack>
              <GradePill score={avgByType.external} />
            </Stack>

            {extItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>No External data yet</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Kick off an External pentest to complete your security overview.</Typography>
                <Button variant="contained" onClick={() => navigate('/pentests/create?type=external')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Start External Pentest
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Left: metrics + severity + chips */}
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={4}><StatCard icon={<Inventory2OutlinedIcon />} title="Assets" value={externalRollup.assets} /></Grid>
                        <Grid item xs={4}><StatCard icon={<SettingsEthernetOutlinedIcon />} title="Ports" value={externalRollup.ports} /></Grid>
                        <Grid item xs={4}><StatCard icon={<BugReportOutlinedIcon />} title="Findings" value={externalRollup.exploits} /></Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Exploits by Severity</Typography>
                      <Grid container spacing={1}>
                        {SEV_ORDER.map((s) => (
                          <Grid item xs={6} sm={4} key={`ex-${s}`}>
                            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(SEV_COLORS[s], 0.35) }}>
                              <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="caption" sx={{ color: alpha(SEV_COLORS[s], 0.9), fontWeight: 700 }}>{s.toUpperCase()}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>{externalRollup.exploitsBySev[s]}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>CVEs by Severity</Typography>
                      <Grid container spacing={1}>
                        {SEV_ORDER.map((s) => (
                          <Grid item xs={6} sm={4} key={`cve-${s}`}>
                            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(SEV_COLORS[s], 0.35) }}>
                              <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="caption" sx={{ color: alpha(SEV_COLORS[s], 0.9), fontWeight: 700 }}>{s.toUpperCase()}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>{externalRollup.cves[s]}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Open Ports</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {externalRollup.portsList.length ? externalRollup.portsList.map((name) => (
                          <Chip key={name} label={name} size="small" sx={{ bgcolor: alpha('#111', 0.06) }} />
                        )) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No ports recorded.</Typography>}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Technology Stack</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {externalRollup.techList.length ? externalRollup.techList.map((name) => (
                          <Chip key={name} label={name} size="small" sx={{ bgcolor: alpha('#111', 0.06) }} />
                        )) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No technologies recorded.</Typography>}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right: tables (Most Recent / Top / Recent Pentests) */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Most Recent Findings (10)</Typography>
                          {recentExternal?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>When</TableCell></TableRow></TableHead>
                              <TableBody>
                                {recentExternal.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.when ? new Date(r.when).toLocaleString() : '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Top Findings (10)</Typography>
                          {topExternal?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                              <TableBody>
                                {topExternal.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent Pentests (5)</Typography>
                          {extRecent5.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Grade</TableCell><TableCell>Findings</TableCell></TableRow></TableHead>
                              <TableBody>
                                {extRecent5.map((r) => (
                                  <TableRow key={r.id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell><Chip size="small" label={r.grade} sx={{ bgcolor: alpha(getGradeColor(r.grade), 0.12), color: getGradeColor(r.grade) }} /></TableCell>
                                    <TableCell>{r.findings}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No pentests to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Card>

          {/* Web */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(TYPE_META.web.color, 0.12), color: TYPE_META.web.color }}>
                  <LanguageIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Web</Typography>
              </Stack>
              <GradePill score={avgByType.web} />
            </Stack>

            {webItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>No Web data yet</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Kick off a Web pentest to complete your security overview.</Typography>
                <Button variant="contained" onClick={() => navigate('/pentests/create?type=web')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Start Web Pentest
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}><StatCard title="Endpoints" value={webRollup.endpoints} /></Grid>
                        <Grid item xs={6}><StatCard title="Findings" value={webRollup.exploits.critical + webRollup.exploits.high + webRollup.exploits.medium + webRollup.exploits.low + webRollup.exploits.info} /></Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Findings by Severity</Typography>
                      <Grid container spacing={1}>
                        {SEV_ORDER.map((s) => (
                          <Grid item xs={6} sm={4} key={`web-${s}`}>
                            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(SEV_COLORS[s], 0.35) }}>
                              <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="caption" sx={{ color: alpha(SEV_COLORS[s], 0.9), fontWeight: 700 }}>{s.toUpperCase()}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>{webRollup.exploits[s]}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* HTTP Methods bar */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>HTTP Methods</Typography>
                      <Box sx={{ height: 200 }}>
                        <Bar
                          data={httpMethodsBar(webRollup.methods)}
                          options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Most Recent Findings (10)</Typography>
                          {recentWeb?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>When</TableCell></TableRow></TableHead>
                              <TableBody>
                                {recentWeb.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.when ? new Date(r.when).toLocaleString() : '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Top Findings (10)</Typography>
                          {topWeb?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                              <TableBody>
                                {topWeb.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent Pentests (5)</Typography>
                          {webRecent5.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Grade</TableCell><TableCell>Findings</TableCell></TableRow></TableHead>
                              <TableBody>
                                {webRecent5.map((r) => (
                                  <TableRow key={r.id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell><Chip size="small" label={r.grade} sx={{ bgcolor: alpha(getGradeColor(r.grade), 0.12), color: getGradeColor(r.grade) }} /></TableCell>
                                    <TableCell>{r.findings}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No pentests to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Card>

          {/* API */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(TYPE_META.api.color, 0.12), color: TYPE_META.api.color }}>
                  <LanguageIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>API</Typography>
              </Stack>
              <GradePill score={avgByType.api} />
            </Stack>

            {apiItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>No API data yet</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Kick off an API pentest to complete your security overview.</Typography>
                <Button variant="contained" onClick={() => navigate('/pentests/create?type=api')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Start API Pentest
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}><StatCard title="Endpoints" value={apiRollup.endpoints} /></Grid>
                        <Grid item xs={6}><StatCard title="Findings" value={apiRollup.exploits.critical + apiRollup.exploits.high + apiRollup.exploits.medium + apiRollup.exploits.low + apiRollup.exploits.info} /></Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Findings by Severity</Typography>
                      <Grid container spacing={1}>
                        {SEV_ORDER.map((s) => (
                          <Grid item xs={6} sm={4} key={`api-${s}`}>
                            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(SEV_COLORS[s], 0.35) }}>
                              <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="caption" sx={{ color: alpha(SEV_COLORS[s], 0.9), fontWeight: 700 }}>{s.toUpperCase()}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>{apiRollup.exploits[s]}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* HTTP Methods bar */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>HTTP Methods</Typography>
                      <Box sx={{ height: 200 }}>
                        <Bar
                          data={httpMethodsBar(apiRollup.methods)}
                          options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Most Recent Findings (10)</Typography>
                          {recentAPI?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>When</TableCell></TableRow></TableHead>
                              <TableBody>
                                {recentAPI.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.when ? new Date(r.when).toLocaleString() : '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Top Findings (10)</Typography>
                          {topAPI?.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Finding</TableCell><TableCell>Severity</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                              <TableBody>
                                {topAPI.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                      <Chip size="small" label={r.severity || 'n/a'} sx={{ bgcolor: alpha(SEV_COLORS[r.severity] || '#999', 0.12) }} />
                                    </TableCell>
                                    <TableCell>{r.count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No findings to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent Pentests (5)</Typography>
                          {apiRecent5.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Grade</TableCell><TableCell>Findings</TableCell></TableRow></TableHead>
                              <TableBody>
                                {apiRecent5.map((r) => (
                                  <TableRow key={r.id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell><Chip size="small" label={r.grade} sx={{ bgcolor: alpha(getGradeColor(r.grade), 0.12), color: getGradeColor(r.grade) }} /></TableCell>
                                    <TableCell>{r.findings}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No pentests to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Card>

          {/* Vishing */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(TYPE_META.vishing.color, 0.12), color: TYPE_META.vishing.color }}>
                  <PhoneIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Vishing</Typography>
              </Stack>
              <GradePill score={avgByType.vishing} />
            </Stack>

            {vishingItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>No Vishing data yet</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Kick off a Vishing campaign to complete your security overview.</Typography>
                <Button variant="contained" onClick={() => navigate('/pentests/create?type=vishing')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Start Vishing Campaign
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Left */}
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={4}><StatCard icon={<PhoneInTalkOutlinedIcon />} title="Calls — Total" value={vishingRollup.totalCalls} color="#2563eb" /></Grid>
                        <Grid item xs={4}><StatCard icon={<CallEndOutlinedIcon />} title="Calls — Ended" value={vishingRollup.endedCalls} color="#64748b" /></Grid>
                        <Grid item xs={4}><StatCard icon={<PhoneCallbackOutlinedIcon />} title="Calls — Ongoing" value={vishingRollup.ongoingCalls} color="#06b6d4" /></Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}><StatCard icon={<CheckCircleOutlineIcon />} title="Passed" value={vishingRollup.passed} color="#16a34a" /></Grid>
                        <Grid item xs={6}><StatCard icon={<ErrorOutlineIcon />} title="Failed" value={vishingRollup.failed} color="#ef4444" /></Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Scenarios Used</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {vishingRollup.scenariosList.length ? vishingRollup.scenariosList.map((s) => (
                          <Chip key={s} label={s} size="small" sx={{ bgcolor: alpha('#111', 0.06) }} />
                        )) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No scenarios recorded.</Typography>}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Voices Used</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {vishingRollup.voicesList.length ? vishingRollup.voicesList.map((v) => (
                          <Chip key={v} label={v} size="small" sx={{ bgcolor: alpha('#111', 0.06) }} />
                        )) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No voices recorded.</Typography>}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right: stacked with spacing */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Most Recent Failed Users (10)</Typography>
                        {vishingRollup.recentFailedUsers.length ? (
                          <Table size="small">
                            <TableHead><TableRow><TableCell>User</TableCell><TableCell>Scenario</TableCell><TableCell>Voice</TableCell><TableCell>When</TableCell></TableRow></TableHead>
                            <TableBody>
                              {vishingRollup.recentFailedUsers.map((u, i) => (
                                <TableRow key={i}>
                                  <TableCell>{u.name}</TableCell>
                                  <TableCell>{u.scenario || '—'}</TableCell>
                                  <TableCell>{u.voice || '—'}</TableCell>
                                  <TableCell>{u.when ? new Date(u.when).toLocaleString() : '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No failed users recorded yet.</Typography>}
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent Pentests (5)</Typography>
                          {visRecent5.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Grade</TableCell><TableCell>Findings</TableCell></TableRow></TableHead>
                              <TableBody>
                                {visRecent5.map((r) => (
                                  <TableRow key={r.id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell><Chip size="small" label={r.grade} sx={{ bgcolor: alpha(getGradeColor(r.grade), 0.12), color: getGradeColor(r.grade) }} /></TableCell>
                                    <TableCell>{r.findings}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No pentests to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Card>

          {/* Dark Web */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(TYPE_META.darkweb.color, 0.12), color: TYPE_META.darkweb.color }}>
                  <LockIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Dark Web Monitoring</Typography>
              </Stack>
              <GradePill score={avgByType.darkweb} />
            </Stack>

            {darkwebItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>No Dark Web data yet</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Enable dark web monitoring to complete your overview.</Typography>
                <Button variant="contained" onClick={() => navigate('/pentests/create?type=darkweb')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                  Start Dark Web Monitoring
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Left: stats */}
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><StatCard title="Total Leaks" value={darkwebRollup.totalLeaks} /></Grid>
                    <Grid item xs={6}><StatCard title="Emails Found" value={darkwebRollup.totalEmails} /></Grid>
                    <Grid item xs={6}><StatCard title="Clear-text Pwds" value={darkwebRollup.clearText} /></Grid>
                    <Grid item xs={6}><StatCard title="Hashed Pwds" value={darkwebRollup.hashed} /></Grid>
                  </Grid>
                </Grid>

                {/* Right column: Leak Sources then Recent Pentests (with spacing) */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Leak Sources (Top 10)</Typography>
                        {darkwebRollup.sourceRows.length ? (
                          <Table size="small">
                            <TableHead><TableRow><TableCell>Source</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                            <TableBody>
                              {darkwebRollup.sourceRows.map((r, i) => (
                                <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell>{r.count}</TableCell></TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No leak sources found.</Typography>}
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Recent Pentests (5)</Typography>
                          {darkRecent5.length ? (
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Grade</TableCell><TableCell>Findings</TableCell></TableRow></TableHead>
                              <TableBody>
                                {darkRecent5.map((r) => (
                                  <TableRow key={r.id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell><Chip size="small" label={r.grade} sx={{ bgcolor: alpha(getGradeColor(r.grade), 0.12), color: getGradeColor(r.grade) }} /></TableCell>
                                    <TableCell>{r.findings}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No pentests to display.</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Card>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => navigate('/pentests')} sx={{ textTransform: 'none', borderRadius: 2 }}>
            View All Pentests
          </Button>
          <Tooltip title="Back to Organizations">
            <IconButton onClick={() => navigate('/organizations')}><DomainIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default OranizationDashboard;
